/**
 * NOTE: CODEGEN IS USED IN THIS FILE. Careless manual edits may be lost.
 *
 * server/tools/syncDirs.ts keeps this file in sync with:
 *   server/src/shared/util/ErrorCodes.ts
 */

import {TSchema, Type} from "@sinclair/typebox";

// map constants to error codes
// Make sure error codes are up to date with legacy:
// https://github.com/aops-ba/aops-academy/blob/ebdc83078b8f57f9ea6d18142a9480964eb34fb7/api/v1/class.AcademyApi.php#L46
export const ERROR_CODES = {
	E_EMAIL_ALREADY_EXISTS: "E_EMAIL_ALREADY_EXISTS",
	E_FETCH_ERROR: "E_FETCH_ERROR",
	E_HONEYPOT_TRIGGERED: "E_HONEYPOT_TRIGGERED",
	E_INSUFFICIENT_PERMISSIONS: "E_INSUFFICIENT_PERMISSIONS",
	E_INVALID_FIELDS: "E_INVALID_FIELDS",
	E_MISSING_FIELDS: "E_MISSING_FIELDS",
	E_NO_ASSOCIATED_EMAIL: "E_NO_ASSOCIATED_EMAIL",
	E_NO_SESSION: "E_NO_SESSION",
	E_PARSING_ERROR: "E_PARSING_ERROR",
	E_SESSION_EXPIRED: "E_SESSION_EXPIRED",
	E_UNAUTHORIZED: "E_UNAUTHORIZED",
	E_UNKNOWN: "E_UNKNOWN",
	E_INVALID_USER_ID: "E_INVALID_USER_ID",
	E_INVALID_EMAIL: "E_INVALID_EMAIL",
	E_INVALID_COURSE_ID: "E_INVALID_COURSE_ID",
} as const;

type ErrorCodesKeys = keyof typeof ERROR_CODES;
export type ErrorCode = (typeof ERROR_CODES)[ErrorCodesKeys];

// map error codes to error handling information
type ErrorMessageMap = {
	[K in ErrorCode]: {
		message: string;
		httpStatus?: number;
	};
};

export const ERROR_RESPONSE_MAP = {
	E_EMAIL_ALREADY_EXISTS: {
		message: "An account with this email already exists.",
	},
	E_FETCH_ERROR: {
		message: "An unknown server error occurred.  Please try again later.",
	},
	E_HONEYPOT_TRIGGERED: {
		message: "Honeypot triggered",
	},
	E_INSUFFICIENT_PERMISSIONS: {
		message: "Insufficient permissions.",
		httpStatus: 403,
	},
	E_INVALID_FIELDS: {
		message:
			"Invalid fields detected.  Please review the highlighted fields and try again.",
	},
	E_MISSING_FIELDS: {
		message:
			"Missing required fields. Please review the highlighted fields and try again.",
	},
	E_NO_ASSOCIATED_EMAIL: {
		message: "No email associated with this account.",
	},
	E_NO_SESSION: {
		message: "Please log in to continue.",
		httpStatus: 403,
	},
	E_PARSING_ERROR: {
		message: "An error occurred while parsing the response.",
	},
	E_SESSION_EXPIRED: {
		message: "Your session has expired.  Please log in and try again.",
		httpStatus: 401,
	},
	E_INVALID_USER_ID: {
		message: "Invalid user ID.",
	},
	E_INVALID_EMAIL: {
		message: "Invalid email.",
	},
	E_INVALID_COURSE_ID: {
		message: "Invalid course ID.",
	},
	E_UNAUTHORIZED: {
		message: "Unauthorized.",
		httpStatus: 403,
	},
	E_UNKNOWN: {
		message: "An unknown server error occurred.  Please try again later.",
	},
} as const satisfies ErrorMessageMap;

// map error codes to their respective moreInfo schemas if we expect more info.
type ErrorMoreInfoSchemaMap = {
	[K in ErrorCode]?: TSchema;
};

export const ERROR_MORE_INFO_SCHEMA_MAP = {
	E_INVALID_FIELDS: Type.Record(Type.String(), Type.String()),
	E_MISSING_FIELDS: Type.Record(Type.String(), Type.String()),
} as const satisfies ErrorMoreInfoSchemaMap;

export type MoreInfoErrorCode = keyof typeof ERROR_MORE_INFO_SCHEMA_MAP;
